import React from "react";
import { Loader2 } from "lucide-react";
import { getFormsUiStrings, resolveFormsLocale } from "i18n/formsUi";

/**
 * FormActions
 *
 * Footer primitive for paired Cancel + Submit buttons used inside a
 * `Modal`/`ModalOrDrawer`/`Drawer` `footer` slot. The submit button
 * uses the standard HTML `form={formId}` attribute so the browser
 * fires its native submit event (and runs any `onSubmit` on the form)
 * when the user presses Enter inside any input of that form -- even
 * though the button itself lives in the modal footer outside the
 * `<form>` element.
 *
 * This is the single supported submission contract for new forms; see
 * `docs/architecture/forms-standard.md` section 2.
 *
 * - `formId` MUST match the id of the `<form>` rendered in the modal
 *   body. `Modal`/`ModalOrDrawer`/`Drawer` accept the same id via
 *   their own `formId` prop and expose it as a `data-form-id` attribute
 *   on the surface for screen-reader and devtools association.
 * - Cancel is `type="button"` so Enter on a focused input never fires
 *   it accidentally.
 * - Loading state is announced via `aria-busy` and a visible spinner.
 *
 * Localized labels default to `formsUi` (Cancel, Submit, Submitting...);
 * call sites override `submitLabel`/`cancelLabel`/`submittingLabel` for
 * domain-specific copy ("Sign in", "Sign up", "Reset password", ...).
 */
export interface FormActionsProps {
  /**
   * id of the `<form>` element being controlled when
   * `submitButtonType === "submit"` (the default). Ignored when the
   * primary button is rendered as `type="button"` (e.g. a "Done"
   * affordance after a successful inline-success state where there
   * is no live form to submit).
   */
  formId: string;
  /** Optional override for the Submit button label. */
  submitLabel?: string;
  /** Optional override for the Cancel button label. */
  cancelLabel?: string;
  /** Optional override for the Submitting... progress label. */
  submittingLabel?: string;
  /** Disable the submit button (typically `!canSubmit`). */
  submitDisabled?: boolean;
  /** Reflects in-flight submission. Drives spinner + aria-busy. */
  submitting?: boolean;
  /** Cancel handler. Should close the modal (or call onClose). */
  onCancel: () => void;
  /**
   * Optional override for the submit click handler. Almost never used
   * because the button's `form` attribute already triggers native form
   * submit -- but required when `submitButtonType === "button"` since
   * there is no implicit submit to dispatch.
   */
  onSubmit?: () => void;
  /** Hide the cancel button. Useful for fully-modal flows. */
  hideCancel?: boolean;
  /**
   * Type of the primary button. Defaults to `"submit"` (paired with
   * `formId` for browser-native Enter-key submit). Pass `"button"`
   * for terminal-state primary actions like a post-success "Done"
   * that just closes the surface -- in that case `onSubmit` is the
   * click handler.
   */
  submitButtonType?: "submit" | "button";
  /** Optional extra class names appended to the wrapper. */
  className?: string;
  /** Override the wrapper layout. Defaults to the standard auth modal layout. */
  wrapperClassName?: string;
}

const FormActions: React.FC<FormActionsProps> = ({
  formId,
  submitLabel,
  cancelLabel,
  submittingLabel,
  submitDisabled = false,
  submitting = false,
  onCancel,
  onSubmit,
  hideCancel = false,
  submitButtonType = "submit",
  className,
  wrapperClassName,
}) => {
  const ui = getFormsUiStrings(resolveFormsLocale());
  const resolvedSubmitLabel = submitLabel ?? ui.submit;
  const resolvedCancelLabel = cancelLabel ?? ui.cancel;
  const resolvedSubmittingLabel = submittingLabel ?? ui.submitting;

  const wrapper =
    wrapperClassName ??
    "flex flex-grow flex-col pb-2 justify-end place-items-end mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0";

  const submitClasses = `w-full sm:w-fit py-2 px-4 ${
    submitDisabled || submitting ? "btn-primary-disabled" : "btn-primary"
  } ${submitting ? "opacity-50 cursor-not-allowed" : ""} ${className ?? ""}`.trim();

  // The primary button is rendered as a centered SPINNER ONLY during
  // submission (no text, no ellipses). Every primary auth/form
  // button goes through this path so the loading visual is identical
  // across the app -- see `docs/architecture/forms-standard.md`
  // section 4 on primary-button consistency.
  const renderPrimaryContent = () =>
    submitting ? (
      <span className="inline-flex items-center justify-center gap-2 w-full" aria-hidden="true">
        <Loader2 className="size-4 animate-spin" />
        <span className="sr-only">{resolvedSubmittingLabel}</span>
      </span>
    ) : (
      resolvedSubmitLabel
    );

  return (
    <div className={wrapper}>
      {!hideCancel && (
        <button
          type="button"
          className="btn-secondary w-full sm:w-fit py-2 px-4"
          onClick={onCancel}
        >
          {resolvedCancelLabel}
        </button>
      )}
      {submitButtonType === "submit" ? (
        <button
          type="submit"
          form={formId}
          disabled={submitDisabled || submitting}
          aria-busy={submitting || undefined}
          onClick={onSubmit}
          className={submitClasses}
        >
          {renderPrimaryContent()}
        </button>
      ) : (
        <button
          type="button"
          disabled={submitDisabled || submitting}
          aria-busy={submitting || undefined}
          onClick={onSubmit}
          className={submitClasses}
        >
          {renderPrimaryContent()}
        </button>
      )}
    </div>
  );
};

export default FormActions;
